// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Connection } from '@shared/commons/Connection';
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import TerceraBaseWindowTemplate from '@front/templates/TerceraBaseWindowTemplate.html';
import TerceraChangePasswordScreenTemplate from '@front/templates/TerceraChangePasswordScreenTemplate.html';
import { AdditionalVerificationScreen } from '../screen/AdditionalVerificationScreen';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { TerceraMessageBox } from './TerceraMessageBox';
import { TerceraWindowBase } from './TerceraWindowBase';
import { DataCache } from '@shared/commons/DataCache';
import { TerceraButtonStyle } from '@shared/utils/Enums/ButtonEnums';
import { ChangePasswordStatusEnum } from '@shared/utils/Enums/ChangePasswordStatusEnum';
import { BusinessRejectError, BusinessRejectErrorDictionary } from '@shared/utils/BusinessRejectUtils';
export class TerceraChangePasswordScreen extends TerceraWindowBase {
    constructor() {
        super(...arguments);
        this.sessionId = '';
        this.forceCloseOnLogout = false;
    }
    oninit() {
        super.oninit();
        void this.set({
            curPwdLabel: Resources.getResource('screen.changepass.old'),
            newPwdLabel: Resources.getResource('screen.changepass.new'),
            newPwdConfirmLabel: Resources.getResource('screen.changepass.confirm'),
            curPwdPlaceholder: Resources.getResource('screen.changepass.watermark_old'),
            newPwdPlaceholder: Resources.getResource('screen.changepass.watermark_new'),
            newPwdConfirmPlaceholder: Resources.getResource('screen.changepass.watermark_confirm'),
            okText: Resources.getResource('screen.changepass.ok'),
            cancelText: Resources.getResource('screen.changepass.cancel'),
            allowPreviewPassword: DataCache.AllowPreviewPassword
        });
        this.on('ok', this.onInputDataConfirmed);
        this.on('cancel', this.close);
    }
    oncomplete() {
        super.oncomplete();
        this.center();
    }
    onInputDataConfirmed() {
        const newPwd = this.get('newPwd');
        const newPwdConfirm = this.get('newPwdConfirm');
        if (newPwd !== newPwdConfirm) {
            this.ShowMsg(Resources.getResource('screen.changepass.alert_not_match'), () => {
                this.clearFields();
            });
            return;
        }
        this.isTradingPassword ? this.changeTradingPassword() : this.changePassword();
    }
    changePassword() {
        Connection.ChangePassword(this.get('curPwd'), this.get('newPwd'), this.UserID, 
        /* verificationPassword */ null).then(this.changePasswordRusult.bind(this));
    }
    changePasswordRusult(result) {
        if (result === null) {
            return;
        }
        if (result.RejectCode) {
            const rjKeY = 'BusinessRejectMessage.' + result.RejectCode;
            let msg = result.errText;
            if (Resources.IsResourcePresentEN(rjKeY)) {
                msg = Resources.getResource(rjKeY);
            }
            this.ShowMsg(msg, () => {
                this.clearFields();
            });
            return;
        }
        const status = result.Status;
        switch (status) {
            case ChangePasswordStatusEnum.STATUS_ACCEPTED:
                this.ShowMsg(Resources.getResource('screen.changepass.changed.login'));
                // TODO Ugly
                Connection.lastConnectionAttemptResponse = { text: '' };
                this.close(this, true);
                Connection.disconnect();
                return;
            case ChangePasswordStatusEnum.STATUS_NEED_VERIFICATION: {
                const okCallBack = function (verificationPassword) {
                    Connection.ChangePassword(this.get('curPwd'), this.get('newPwd'), this.UserID, verificationPassword).then(this.changePasswordRusult.bind(this));
                }.bind(this);
                const cancelCallBack = function () {
                    this.close();
                }.bind(this);
                AdditionalVerificationScreen.Show(null, okCallBack, cancelCallBack, {
                    allowPreviewPassword: this.get('allowPreviewPassword')
                });
                return;
            }
            case ChangePasswordStatusEnum.STATUS_WRONG_VERIFICATION:
                this.ShowMsg(Resources.getResource('screen.changepass.wrongVerification'));
                this.close();
                // Connection.disconnect();    // #99859 (commented out due to #115329)
                return;
            case ChangePasswordStatusEnum.STATUS_STRONG_PAS_LOW: {
                const newPasswordIsEmpty = !isValidString(this.get('newPwd'));
                const MsgLow = newPasswordIsEmpty
                    ? Resources.getResource(BusinessRejectErrorDictionary[BusinessRejectError.NEW_PASS_EMPTY])
                    : Resources.getResource('system.ChangePassLow').replace('{0}', result.MinLength);
                this.ShowMsg(MsgLow, () => {
                    this.clearFields();
                });
                return;
            }
            case ChangePasswordStatusEnum.STATUS_STRONG_PAS_BASE: {
                const MsgBase = Resources.getResource('system.ChangePassBase').replace('{0}', result.MinLength);
                this.ShowMsg(MsgBase, () => {
                    this.clearFields();
                });
                return;
            }
            case ChangePasswordStatusEnum.STATUS_STRONG_PAS_HIGH: {
                const MsgHigh = Resources.getResource('system.ChangePassHigh').replace('{0}', result.MinLength);
                this.ShowMsg(MsgHigh, () => {
                    this.clearFields();
                });
                return;
            }
            case ChangePasswordStatusEnum.STATUS_LOCKED_AFTER_BRUT_FORCE:
                this.ShowMsg(Resources.getResource('screen.changepass.lockedAfterBrut'), () => {
                    this.clearFields();
                });
                return;
            case ChangePasswordStatusEnum.STATUS_WRONG_OLD_OR_NEW_PASSWORD:
                this.ShowMsg(Resources.getResource('screen.changepass.wrongOldOrNewPassword'), () => {
                    this.clearFields();
                });
                return;
            case ChangePasswordStatusEnum.STATUS_PWD_MUST_DE_DIFFERENT:
                this.ShowMsg(Resources.getResource('screen.changepass.pwdMustBeDifferent'), () => {
                    this.clearFields();
                });
                return;
            case ChangePasswordStatusEnum.STATUS_WRONG_MAX_LENGTH:
                this.ShowMsg(Resources.getResource('system.TooLongPass').replace('{0}', result.MaxLength), () => {
                    this.clearFields();
                });
                return;
            case ChangePasswordStatusEnum.STATUS_PWD_IS_TOO_EASY:
                this.ShowMsg(Resources.getResource('screen.changepass.tooeasy'), () => {
                    this.clearFields();
                });
        }
    }
    clearFields() {
        void this.set({
            curPwd: '',
            newPwd: '',
            newPwdConfirm: ''
        });
    }
    changeTradingPassword() {
        Connection.ChangeTradingPassword(this.get('curPwd'), this.get('newPwd')).then((result) => {
            this.clearFields();
            if (result === null) {
                this.close(this, true);
                return;
            }
            if (result.RejectCode) {
                this.ShowMsg(Resources.getResource('BusinessRejectMessage.' + result.RejectCode));
                return;
            }
            if (result.Status === ChangePasswordStatusEnum.STATUS_ACCEPTED) {
                this.ShowMsg(Resources.getResource('screen.changepass.changed'));
            }
            if (result.Status === ChangePasswordStatusEnum.STATUS_WRONG_MAX_LENGTH) {
                this.ShowMsg(Resources.getResource('system.TooLongPass').replace('{0}', result.MaxLength));
            }
            this.close(this, true);
        });
    }
    close(_sender, fromOk = false) {
        if (!fromOk && this.cancelCallBack != null) {
            this.cancelCallBack();
        }
        super.close();
    }
    setScreenData(UserID, OldPass, cancelCB, isTradingPassword) {
        this.on('teardown', () => {
            MainWindowManager.ChangePasswordScreen = null;
        });
        void this.set({
            visible: true,
            header: isTradingPassword
                ? Resources.getResource('screen.changeTradingPass.title')
                : Resources.getResource('screen.changepass.title')
        });
        const mainAcc = DataCache.MainAccountNew;
        if (UserID === '' && mainAcc != null) {
            UserID = mainAcc.userID;
        }
        this.UserID = UserID !== null && UserID !== void 0 ? UserID : DataCache.getUserID();
        this.OldPass = OldPass;
        this.cancelCallBack = cancelCB;
        this.isTradingPassword = isTradingPassword !== null && isTradingPassword !== void 0 ? isTradingPassword : false;
    }
    static show(UserID, OldPass, cancelCB, isTradingPassword = false) {
        if (MainWindowManager.ChangePasswordScreen != null) {
            MainWindowManager.ChangePasswordScreen.setFocus();
        }
        else {
            const scr = new TerceraChangePasswordScreen();
            MainWindowManager.ChangePasswordScreen = scr;
            scr.setScreenData(UserID, OldPass, cancelCB, isTradingPassword);
            MainWindowManager.AddControl(scr);
        }
    }
    ShowMsg(text, closeHandler) {
        if (!closeHandler) {
            closeHandler = null;
        }
        const additionalData = { okText: Resources.getResource('general.messageBox.ok') };
        TerceraMessageBox.Show(this.get('header'), text, MessageBoxType.Info, closeHandler, closeHandler, false, true, null, additionalData, true);
    }
}
TerceraWindowBase.extendWith(TerceraChangePasswordScreen, {
    template: TerceraBaseWindowTemplate,
    data: function () {
        return {
            movable: false,
            showFooter: false,
            resizable: false,
            alwaysMiddle: true,
            focused: true,
            left: 0,
            top: 0,
            width: 400,
            style_addition_jsWin: 'change-password-screen-addition',
            curPwd: '',
            newPwd: '',
            newPwdConfirm: '',
            allowPreviewPassword: false,
            btnOKStyle: TerceraButtonStyle.Blue,
            btnCancelStyle: TerceraButtonStyle.Standard
        };
    },
    partials: {
        bodyPartial: TerceraChangePasswordScreenTemplate
    }
});
